import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { listChapters } from '../instructor/instructorApi';
import {
  activateCompetition,
  deactivateCompetition,
  getCompetitionState,
  getLeaderboard,
  resetCompetition,
} from './leaderboardApi';

const POLL_INTERVAL_MS = 3000;

/** Fetches chapters, competition state and (while active) leaderboard standings for a course, polling every ~3s. */
export function useCompetitionQueries(courseId: string) {
  const chaptersQuery = useQuery({
    queryKey: ['leaderboard', 'chapters', courseId],
    queryFn: () => listChapters(courseId),
    enabled: Boolean(courseId),
  });

  const stateQuery = useQuery({
    queryKey: ['leaderboard', 'competition-state', courseId],
    queryFn: () => getCompetitionState(courseId),
    enabled: Boolean(courseId),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const isActive = stateQuery.data?.active ?? false;

  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', 'standings', courseId],
    queryFn: () => getLeaderboard(courseId),
    enabled: Boolean(courseId) && stateQuery.isSuccess,
    // Only poll while competition mode is active; frozen standings stop updating (US7 acceptance).
    refetchInterval: isActive ? POLL_INTERVAL_MS : false,
  });

  return { chaptersQuery, stateQuery, leaderboardQuery };
}

/**
 * Activate/reset/deactivate competition-mode mutations. On success they invalidate the polled
 * queries above and the nav's active-challenge query, so the "Challenge" link and its live dot
 * update immediately instead of on the next poll.
 */
export function useCompetitionMutations(courseId: string) {
  const queryClient = useQueryClient();

  function invalidateCompetitionQueries() {
    void queryClient.invalidateQueries({ queryKey: ['leaderboard', 'competition-state', courseId] });
    void queryClient.invalidateQueries({ queryKey: ['leaderboard', 'standings', courseId] });
    void queryClient.invalidateQueries({ queryKey: ['nav', 'active-challenge'] });
  }

  const activateMutation = useMutation({
    mutationFn: (chapterId?: string) => activateCompetition(courseId, chapterId),
    onSuccess: invalidateCompetitionQueries,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deactivateCompetition(courseId),
    onSuccess: invalidateCompetitionQueries,
  });

  const resetMutation = useMutation({
    mutationFn: () => resetCompetition(courseId),
    onSuccess: invalidateCompetitionQueries,
  });

  return { activateMutation, deactivateMutation, resetMutation };
}
